import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Shield,
  User,
  HeartHandshake,
  Search,
  Loader2,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

type TeamMemberRole = "coach" | "player" | "parent";

type TeamRow = {
  id: string;
  team_name: string | null;
  league: string | null;
  archived?: boolean | null;
};

type MembershipRow = {
  id: string;
  team_id: string;
  role: TeamMemberRole;
  status: string;
  teams: TeamRow | TeamRow[] | null;
};

type HubTeam = {
  id: string;
  team_name: string;
  league: string | null;
  role: TeamMemberRole;
};

function cx(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function roleLabel(role: TeamMemberRole) {
  if (role === "coach") return "Coach";
  if (role === "player") return "Joueur";
  return "Parent";
}

function roleBadgeClass(role: TeamMemberRole) {
  if (role === "coach") {
    return "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100";
  }
  if (role === "player") {
    return "bg-blue-100 text-blue-900 dark:bg-blue-900/20 dark:text-blue-200";
  }
  return "bg-purple-100 text-purple-900 dark:bg-purple-900/20 dark:text-purple-200";
}

function RoleIcon({ role }: { role: TeamMemberRole }) {
  if (role === "coach") return <Shield className="h-3.5 w-3.5" />;
  if (role === "player") return <User className="h-3.5 w-3.5" />;
  return <HeartHandshake className="h-3.5 w-3.5" />;
}

export default function MesEquipesHub() {
  const { clubId } = useParams<{ clubId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [teams, setTeams] = useState<HubTeam[]>([]);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"all" | TeamMemberRole>("all");

  useEffect(() => {
    let cancelled = false;

    async function loadTeams() {
      if (!clubId) {
        setError("Club introuvable.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError) throw authError;
        if (!user) throw new Error("Utilisateur non connecté.");

        const [{ data, error }, { data: ownedTeams, error: ownedErr }] = await Promise.all([
          supabase
            .from("team_members")
            .select(`
              id,
              team_id,
              role,
              status,
              teams:team_id (
                id,
                team_name,
                league,
                archived
              )
            `)
            .eq("user_id", user.id)
            .eq("status", "active")
            .in("role", ["coach", "player", "parent"]),
          // Le rôle "owner" n'est pas stocké dans team_members : il se déduit de teams.user_id.
          // Sans ça, une équipe qu'on possède mais dont on n'est pas explicitement "coach"
          // n'apparaîtrait jamais ici une fois rattachée au club.
          supabase
            .from("teams")
            .select("id, team_name, league")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .or("archived.is.null,archived.eq.false"),
        ]);

        if (error) throw error;
        if (ownedErr) throw ownedErr;

        const mapped: HubTeam[] = ((data || []) as MembershipRow[])
          .filter((row) => {
            const team = Array.isArray(row.teams) ? row.teams[0] : row.teams;
            return !!team?.id && !team.archived;
          })
          .map((row) => {
            const team = Array.isArray(row.teams) ? row.teams[0] : row.teams;

            return {
              id: team!.id,
              team_name: team!.team_name?.trim() || "Équipe sans nom",
              league: team!.league || null,
              role: row.role,
            };
          });

        const ownedMapped: HubTeam[] = (ownedTeams || []).map((team: TeamRow) => ({
          id: team.id,
          team_name: team.team_name?.trim() || "Équipe sans nom",
          league: team.league || null,
          role: "coach",
        }));

        const deduped = Array.from(
          new Map(
            [...mapped, ...ownedMapped].map((item) => [`${item.id}-${item.role}`, item])
          ).values()
        );

        if (cancelled) return;
        setTeams(deduped);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || "Impossible de charger les équipes.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTeams();

    return () => {
      cancelled = true;
    };
  }, [clubId]);

  const filteredTeams = useMemo(() => {
    const q = search.trim().toLowerCase();

    return teams.filter((team) => {
      const matchesRole = roleFilter === "all" ? true : team.role === roleFilter;
      const matchesSearch =
        !q ||
        team.team_name.toLowerCase().includes(q) ||
        (team.league || "").toLowerCase().includes(q) ||
        roleLabel(team.role).toLowerCase().includes(q);

      return matchesRole && matchesSearch;
    });
  }, [teams, search, roleFilter]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Organisation</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Mes équipes
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Retrouvez ici toutes les équipes auxquelles vous êtes rattaché.
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
          <div className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search className="h-4 w-4 text-gray-500 dark:text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une équipe..."
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-700"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {(["all", "coach", "player", "parent"] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setRoleFilter(f)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm border",
                  roleFilter === f
                    ? "bg-gray-100 text-gray-900 border-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                    : "bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                )}
              >
                {f === "all"
                  ? "Tout"
                  : f === "coach"
                    ? "Coach"
                    : f === "player"
                      ? "Joueur"
                      : "Parent"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des équipes...
          </div>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 text-sm text-gray-500 dark:text-gray-400">
          Aucune équipe trouvée.
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {filteredTeams.map((team) => (
            <Link
              key={`${team.id}-${team.role}`}
              to={`/club/${clubId}/team/${team.id}`}
              className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {team.team_name}
                  </div>

                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {team.league || "Championnat non renseigné"}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      className={cx(
                        "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                        roleBadgeClass(team.role)
                      )}
                    >
                      <RoleIcon role={team.role} />
                      {roleLabel(team.role)}
                    </span>
                  </div>
                </div>

                <div className="shrink-0 text-gray-400 dark:text-gray-500">
                  <ChevronRight className="h-5 w-5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}